import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { StateWorkspaceSummary } from '@/lib/apiTypes'
import { ServeOverrideBadge } from '@/shared/components/ServeOverrideControl'
import { BlockedBadge } from '@/shared/components/BlockedBadge'
import { api } from '@/lib/api'
import { useToast } from '@/context/ToastContext'
import { HclSnippet, buildBackendHcl } from '@/shared/components/HclSnippet'
import { formatDateTime, relativeTime, formatCount } from '@/lib/format'
import { ScanStatusBadge } from '@/shared/components/ScanStatusBadge'
import { PushedByCell } from '@/features/state/components/PushedByCell'
import { Modal } from '@/shared/components/Modal'
import { Tooltip } from '@/shared/components/Tooltip'
import { Badge } from '@/shared/components/Badge'
import { ResourceSplitBar } from '@/shared/components/ResourceSplitBar'
import { IconButton } from '@/shared/components/IconButton'
import { SeverityDonut } from '@/shared/components/SeverityDonut'
import { SettingsIcon, RescanIcon } from '@/shared/components/Icons'

export function WorkspaceRow({ orgId, token, ws, onOpen, onRefresh }: {
  orgId: string
  token: string | null
  ws: StateWorkspaceSummary
  onOpen: () => void
  onRefresh: () => void
}) {
  const { showToast } = useToast()
  const [rescanBusy, setRescanBusy] = useState(false)
  const [showConfig, setShowConfig] = useState(false)
  const pushedAgo = ws.pushed_at ? relativeTime(ws.pushed_at) : null
  const unscanned = ws.vuln_count === 0 && ws.critical_count === 0 && ws.high_count === 0

  async function handleRescan() {
    setRescanBusy(true)
    try {
      await api.post(`/api/v1/${orgId}/state/${encodeURIComponent(ws.name)}/rescan`, {})
      showToast('Rescan queued.', 'success')
      onRefresh()
    } catch (err) {
      showToast((err as Error).message, 'error')
    } finally {
      setRescanBusy(false)
    }
  }

  return (
    <>
      {showConfig && (
        <Modal track="workspace_backend_config" title={ws.name} kicker="// backend config" onClose={() => setShowConfig(false)} maxWidth={800}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <HclSnippet code={buildBackendHcl(orgId, ws.name, orgId, token)} />
            <ol style={{ fontFamily: 'var(--mono)', fontSize: '0.68rem', color: 'var(--text3)', lineHeight: 1.8, margin: 0, paddingLeft: '1.2em', display: 'flex', flexDirection: 'column', gap: 4 }}>
              <li>
                Authenticate with a <Link to={`/org/${orgId}/tokens`} style={{ color: 'var(--accent)' }}>bot token</Link>
                {' '}or, if running from GitHub Actions / GitLab CI, use{' '}
                <Link to="/integrations/oidc-trust" style={{ color: 'var(--accent)' }}>Pipeline Trust</Link>
                {' '}- no stored secrets needed.
              </li>
              <li>Set <span style={{ color: 'var(--green)' }}>password</span> to the token secret.</li>
              <li>Set <span style={{ color: 'var(--green)' }}>username</span> to any non-empty string - it is required by Terraform but ignored by the API.</li>
            </ol>
          </div>
        </Modal>
      )}
      <tr
        className="row-link"
        role="link"
        tabIndex={0}
        aria-label={`Open ${ws.name} workspace detail`}
        onClick={onOpen}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) { e.preventDefault(); onOpen() }
        }}
      >
      <td>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
          <code className="mono" style={{ fontSize: '0.8rem' }}>{ws.name}</code>
          {ws.lock_id && (
            <Tooltip text={`Locked by ${ws.lock_who ?? 'unknown'}`} placement="top">
              <span tabIndex={0} style={{ display: 'inline-flex', cursor: 'help' }}><Badge variant="amber">🔒 locked</Badge></span>
            </Tooltip>
          )}
          {ws.serve_override
            ? <ServeOverrideBadge value={ws.serve_override} reason={ws.serve_override_reason} />
            : ws.blocked === 1 && (
              <BlockedBadge label="✗ blocked" policySummary="An enforcing policy blocks the next apply's LOCK until attested." />
            )}
        </span>
      </td>
      <td>
        {/* Just the serial: a stored-version count alongside it would read as a second,
            independent number while being identical on any workspace whose history began
            here - Terraform increments the serial once per apply. */}
        {ws.latest_serial === null ? (
          <Tooltip text="No state version pushed yet." placement="top">
            <span tabIndex={0} style={{ color: 'var(--text3)', cursor: 'help' }}>&mdash;</span>
          </Tooltip>
        ) : (
          <Tooltip text={`The newest state version is serial ${ws.latest_serial}.`} placement="top">
            <span tabIndex={0} style={{ fontFamily: 'var(--mono)', fontSize: '0.8rem', cursor: 'help' }}>
              {ws.latest_serial}
            </span>
          </Tooltip>
        )}
      </td>
      <td>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontFamily: 'var(--mono)', fontSize: '0.8rem' }}>{formatCount(ws.resource_count)}</span>
          <ResourceSplitBar real={ws.real_resource_count} internal={ws.internal_resource_count} compact />
        </span>
      </td>
      <td>
        {ws.latest_serial !== null
          ? <ScanStatusBadge status={ws.scan_status ?? ''} />
          : <span className="dim">—</span>}
      </td>
      <td>
        <SeverityDonut
          critical={ws.critical_count}
          high={ws.high_count}
          total={ws.vuln_count}
          footnote={ws.latest_serial != null ? `Latest version only (serial ${ws.latest_serial}) - an older version may carry different findings.` : undefined}
        />
        {unscanned && (
          <Tooltip
            text={
              ws.latest_serial == null
                ? 'No version pushed yet.'
                : ws.scan_status === 'published'
                  ? `No findings on the latest version (serial ${ws.latest_serial}).`
                  : 'Latest version not scanned yet.'
            }
            placement="top"
          >
            <span tabIndex={0} style={{ color: 'var(--text3)', cursor: 'help' }}>—</span>
          </Tooltip>
        )}
      </td>
      <td>
        {ws.policy_total_count === 0
          ? <span style={{ color: 'var(--text3)', fontSize: '0.7rem' }}>—</span>
          : ws.policy_failing_count > 0
            ? <span className="badge badge-red">{ws.policy_failing_count} failing</span>
            : <span className="badge badge-green">✓ compliant</span>}
      </td>
      <td onClick={(e) => e.stopPropagation()}>
        {ws.actor_type ? (
          <PushedByCell
            actorType={ws.actor_type}
            actorName={ws.actor_name}
            vcsProvider={ws.vcs_provider}
            vcsRepository={ws.vcs_repository}
            vcsRef={ws.vcs_ref}
            vcsRefType={ws.vcs_ref_type}
            vcsSha={ws.vcs_sha}
            vcsCommitUrl={ws.vcs_commit_url}
          />
        ) : (
          <span className="dim">—</span>
        )}
      </td>
      <td style={{ fontSize: '0.78rem', color: 'var(--text2)' }} onClick={(e) => e.stopPropagation()}>
        {ws.pushed_at ? (
          <Tooltip text={formatDateTime(ws.pushed_at)} placement="top">
            <span tabIndex={0} className="cursor-help">{pushedAgo}</span>
          </Tooltip>
        ) : (
          <span className="dim">—</span>
        )}
      </td>
      <td onClick={(e) => e.stopPropagation()}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
          <IconButton label="Backend cfg" tip="Terraform backend configuration for this workspace" onClick={() => setShowConfig(true)}>
            <SettingsIcon />
          </IconButton>
          <IconButton label="Rescan" tip="Re-scan latest state version" busy={rescanBusy} onClick={handleRescan}>
            <RescanIcon />
          </IconButton>
        </span>
      </td>
      </tr>
    </>
  )
}
